import React, { useState, useEffect } from 'react';
import { RAMP_CSS } from '../utils/datasetLayer';
import { ChevronDown, ChevronUp } from 'lucide-react';

function formatValue(v) {
    if (v === null || v === undefined || !Number.isFinite(v)) return '—';
    return Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : String(Math.round(v * 100) / 100);
}

const MapLegend = ({ isH3Visible, h3ActiveView, datasetLegend = null, categories = {}, isClustered = false }) => {
    // Start minimized on both mobile and desktop
    const [isMinimized, setIsMinimized] = useState(true);

    // Update minimized state on window resize
    useEffect(() => {
        const handleResize = () => {
            // Only auto-minimize/expand if user hasn't manually toggled
            // This prevents annoying auto-changes during resize
            const isMobile = window.innerWidth <= 768;
            if (isMobile && !isMinimized) {
                // Don't auto-minimize on mobile if user expanded it
                return;
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMinimized]);

    if (isMinimized) {
        return (
            <button
                onClick={() => setIsMinimized(false)}
                aria-label="Open map legend"
                className="absolute map-overlay-bottom-left bg-gray-800 rounded-lg shadow-xl px-3 py-2 z-10 border border-gray-700 flex items-center gap-2 hover:shadow-2xl transition-shadow max-w-[calc(100vw-2rem)]"
            >
                <ChevronUp className="w-4 h-4 flex-shrink-0" />
                {/* Zoomed out, the map is covered in blue circles with a bare
                    number in them and nothing on screen says what the number
                    counts. The explanation used to live inside the legend —
                    which opens minimised, so in practice nobody saw it. While
                    the clusters are on screen the collapsed pill explains them
                    instead, and goes back to saying "Legend" once they are not. */}
                {isClustered ? (
                    <span className="flex items-center gap-1.5 min-w-0">
                        <span
                            className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[9px] font-bold text-white"
                            style={{ backgroundColor: 'rgba(59, 130, 246, 0.85)', clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)' }}
                        >
                            #
                        </span>
                        <span className="text-xs font-medium text-gray-300 truncate">= leads in that area</span>
                    </span>
                ) : (
                    <span className="font-semibold text-sm">Legend</span>
                )}
            </button>
        );
    }

    return (
        <div className="absolute map-overlay-bottom-left bg-gray-800 rounded-xl shadow-2xl p-4 z-10 border border-gray-700 min-w-[200px] max-h-[70%] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-sm text-gray-100">Map Legend</h4>
                <button
                    onClick={() => setIsMinimized(true)}
                    aria-label="Minimise legend"
                    className="text-gray-400 hover:text-gray-300 transition-colors p-1"
                >
                    <ChevronDown className="w-4 h-4" />
                </button>
            </div>

            {isClustered && (
                <div className="border-b border-gray-700 pb-3 mb-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Zoomed out</div>
                    <div className="flex items-start gap-2">
                        <span
                            className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[10px] font-bold text-white"
                            style={{ backgroundColor: 'rgba(59, 130, 246, 0.7)', clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)' }}
                        >
                            #
                        </span>
                        <span className="text-xs text-gray-300 leading-snug">
                            Each area shows how many leads are inside it — the darker it is, the more there are. Zoom in to see them as pins.
                        </span>
                    </div>
                </div>
            )}

            {/* Area dataset layer (org-imported data): colour is the cell's rank, low → high */}
            {datasetLegend && (
                <div className="space-y-1 mb-4" data-testid="dataset-legend">
                    <div className="text-xs font-semibold text-gray-500 uppercase">{datasetLegend.title}</div>
                    <div className="h-2.5 rounded" style={{ background: `linear-gradient(to right, ${RAMP_CSS.join(', ')})` }} />
                    <div className="flex justify-between text-[11px] text-gray-400">
                        <span>{formatValue(datasetLegend.min)}</span>
                        <span>{formatValue(datasetLegend.max)}</span>
                    </div>
                    <p className="text-[11px] text-gray-500">Your data · darker = higher</p>
                </div>
            )}

            {/* H3 Layer Legend */}
            {isH3Visible && (
                <div className="space-y-2 mb-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase">
                        {h3ActiveView === 'sales' ? 'Sales Data'
                            : h3ActiveView === 'revenue' ? 'Transaction Revenue'
                            : h3ActiveView === 'population' ? 'Population Density'
                            : 'Traffic Density'}
                    </div>

                    {h3ActiveView === 'revenue' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 236, 179, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Lowest revenue</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 213, 128, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(255, 183, 77, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Medium</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(251, 140, 0, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">High</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(230, 81, 0, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Highest revenue</span>
                            </div>
                            <div className="text-[10px] text-gray-500 pt-1">
                                Closed-deal revenue per area (zoom in for top product)
                            </div>
                        </div>
                    ) : h3ActiveView === 'sales' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(165, 242, 243, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">¥0 - 100M</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(144, 224, 239, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">¥100M - 500M</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(100, 181, 246, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">¥500M - 1B</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(66, 165, 245, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">¥1B - 2B</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(33, 150, 243, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">¥2B+</span>
                            </div>
                        </div>
                    ) : h3ActiveView === 'population' ? (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(224, 231, 255, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Sparsest</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(165, 180, 252, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(129, 140, 248, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Medium</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(99, 102, 241, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">High</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(67, 56, 202, 0.75)' }}></div>
                                <span className="text-xs text-gray-300">Densest</span>
                            </div>
                            <div className="text-[10px] text-gray-500 pt-1">
                                People/km² per area (grey = no data, e.g. outside Tokyo)
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(227, 242, 253, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">Very Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(187, 222, 251, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">Low</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(144, 202, 249, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">Medium</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(100, 181, 246, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">High</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(66, 165, 245, 0.7)' }}></div>
                                <span className="text-xs text-gray-300">Very High</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Entity Status Legend */}
            <div className="border-t border-gray-700 pt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-3 tracking-wider">Entity Status</div>

                {/* Lead Statuses */}
                <div className="mb-3">
                    <div className="text-[9px] font-bold text-gray-400 mb-1.5 tracking-widest uppercase">LEADS</div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-blue-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">New</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-purple-600 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Planned</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-sky-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Contacted</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-green-600 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Won</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-gray-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Lost</span>
                        </div>
                    </div>
                </div>

                {/* Customer Statuses */}
                <div className="mb-3">
                    <div className="text-[9px] font-bold text-gray-400 mb-1.5 tracking-widest uppercase">CUSTOMERS</div>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Active</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-orange-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">At Risk</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-gray-400 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Inactive</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white shadow-sm"></div>
                            <span className="text-xs text-gray-300">Churned</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Clusters — shown only while the map is actually clustering, so the
                explanation appears exactly when the circles do. This was the
                reported confusion: blue circles with numbers and nothing saying
                what the numbers counted. */}
            {/* Priority */}
            <div className="border-t border-gray-700 pt-3 mt-3">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Priority</div>
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <span className="text-sm">🔥🔥🔥</span>
                        <span className="text-xs text-gray-300">High Priority</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm">⚡⚡</span>
                        <span className="text-xs text-gray-300">Medium Priority</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm">❄️</span>
                        <span className="text-xs text-gray-300">Low Priority</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MapLegend;
